import type { Cents } from "../domain/money";
import type { CostType } from "../domain/proposedCostLine";

/**
 * Document interpretation boundary (Phase 9D, spec §33A).
 *
 * A DocumentInterpreter turns bytes (a receipt photo, a PDF invoice) into a
 * CANDIDATE structured document. It is provider-neutral on purpose: no
 * provider's field names, quirks or auth reach the accounting code, and a
 * future SaaS build performs the same call server-side.
 *
 * The permanent rule this type exists to enforce: **an extraction is a
 * proposal, never accounting truth.** Nothing here writes a Purchase; the
 * user reviews and confirms, and only then do the existing Phase 5 records
 * get created.
 */

export type DocumentKind = "receipt" | "invoice" | "return-receipt";

/** Where a value came from on the page, so it can be highlighted later. */
export interface SourceRegion {
  pageNumber: number;
  /** Flat [x1,y1,x2,y2,…] polygon in `pageUnit` units. */
  polygon: number[];
  pageWidth: number | null;
  pageHeight: number | null;
  /** Provider page unit ("inch" or "pixel"). */
  pageUnit: string;
}

export interface ExtractedValue<T> {
  value: T | null;
  /** Exactly what appeared on the document. */
  raw: string;
  /** 0..1 when the provider reports it. */
  confidence: number | null;
  region: SourceRegion | null;
}

export interface ExtractedLine {
  description: ExtractedValue<string>;
  sku: ExtractedValue<string>;
  upc: ExtractedValue<string>;
  quantityX100: ExtractedValue<number>;
  unit: ExtractedValue<string>;
  unitPriceCents: ExtractedValue<Cents>;
  amountCents: ExtractedValue<Cents>;
  /** Invoice lines may be labor; receipts are normally material. */
  costType: CostType | null;
  /** The line's own region (used for highlighting the whole row). */
  region: SourceRegion | null;
  confidence: number | null;
}

export interface ExtractedDocument {
  /** Local document facts remain review evidence, never proof of bank settlement. */
  evidence?: import("./structuredTextDocument").DocumentEvidence97;
  kind: DocumentKind;
  providerId: string;
  modelId: string;
  apiVersion: string;
  pageCount: number;
  vendor: ExtractedValue<string>;
  documentDate: ExtractedValue<string>;
  receiptNumber: ExtractedValue<string>;
  subtotalCents: ExtractedValue<Cents>;
  discountCents: ExtractedValue<Cents>;
  taxCents: ExtractedValue<Cents>;
  shippingCents: ExtractedValue<Cents>;
  totalCents: ExtractedValue<Cents>;
  paymentMethod: ExtractedValue<string>;
  lines: ExtractedLine[];
}

export interface InterpretRequest {
  bytes: Uint8Array;
  filename: string;
  mimeType: string;
  kind: DocumentKind;
}

export interface DocumentInterpreter {
  id: string;
  displayName: string;
  /** Which file types this interpreter accepts (MIME types). */
  supportedMimeTypes: readonly string[];
  interpret(request: InterpretRequest): Promise<ExtractedDocument>;
  /**
   * Spec §73D: the document's PLAIN TEXT via the provider's layout model,
   * for the pure loan-terms proposer. Optional — a build without it simply
   * cannot read loan agreements and says so.
   */
  readText?(request: Omit<InterpretRequest, "kind">): Promise<string>;
  credentialStatus(): Promise<{ configured: boolean; path: string; note: string | null }>;
}

/* ------------------------------------------------------------------ */
/* File-type support                                                   */
/* ------------------------------------------------------------------ */

export const SUPPORTED_DOCUMENT_MIME_TYPES = [
  "application/pdf",
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/bmp",
  "image/tiff",
  // HEIC/HEIF: the interpreter accepts it, but the app cannot RENDER it
  // for review/highlighting (see canPreviewMime) — surfaced honestly.
  "image/heic",
  "image/heif",
] as const;

export function isSupportedDocumentMime(mime: string): boolean {
  return (SUPPORTED_DOCUMENT_MIME_TYPES as readonly string[]).includes(mime.toLowerCase());
}

/** Can the in-app viewer display this for review + highlighting? */
export function canPreviewMime(mime: string): boolean {
  const m = mime.toLowerCase();
  return m === "application/pdf" || m === "image/jpeg" || m === "image/jpg" || m === "image/png" || m === "image/bmp";
}

/**
 * Honest message for a file we cannot handle, instead of a mysterious
 * failure. HEIC gets its own wording because the provider can read it but
 * the viewer cannot show it.
 */
export function unsupportedDocumentMessage(mime: string, filename: string): string | null {
  const m = mime.toLowerCase();
  if (isSupportedDocumentMime(m)) {
    if (!canPreviewMime(m)) {
      return `${filename} is a HEIC/HEIF image. It can be read for extraction, but Berrier Reno cannot display it for review or receipt highlighting — convert it to JPEG or PNG (on iPhone: Settings → Camera → Formats → Most Compatible) for the full workflow.`;
    }
    return null;
  }
  return `${filename} is not a supported document type. Upload a PDF, JPEG, PNG, BMP or TIFF receipt or invoice.`;
}

/* ------------------------------------------------------------------ */
/* Unavailable interpreter (no credential / browser build)             */
/* ------------------------------------------------------------------ */

export type InterpreterErrorKind =
  | "not-configured"
  | "credentials"
  | "quota"
  | "unsupported"
  | "network"
  | "provider";

export class InterpreterError extends Error {
  constructor(
    readonly kind: InterpreterErrorKind,
    message: string,
  ) {
    super(message);
    this.name = "InterpreterError";
  }
}

export function toInterpreterError(err: unknown): InterpreterError {
  if (err instanceof InterpreterError) return err;
  const raw = (err ?? {}) as { kind?: string; message?: string };
  const kind = (raw.kind ?? "provider") as InterpreterErrorKind;
  return new InterpreterError(
    kind,
    raw.message ?? (typeof err === "string" ? err : "The document could not be interpreted."),
  );
}

export function createUnavailableInterpreter(reason: string): DocumentInterpreter {
  return {
    id: "unavailable",
    displayName: "Not configured",
    supportedMimeTypes: SUPPORTED_DOCUMENT_MIME_TYPES,
    async interpret() {
      throw new InterpreterError("not-configured", reason);
    },
    async credentialStatus() {
      return { configured: false, path: "", note: reason };
    },
  };
}
